/**
 * Fret-scale-degree-in-shape recognition — the CAGED-linked melodic rung.
 *
 * A I–IV–V–I cadence establishes a major key; the tonic-chord CAGED shape is lit
 * on the neck as `given` context (its root/3rd/5th showing do/mi/sol). Then a
 * single scale degree plays; the user taps where that degree sits *inside the
 * shape's box* — filling in the non-chord scale tones (re/fa/la/ti) that complete
 * the scale around the chord.
 *
 * This ties each chord shape to its parent scale: the melodic counterpart to the
 * diatonic CAGED rungs. The shape geometry is built the same way the diatonic
 * rung builds its board (CAGED_SHAPES + resolveVoiceMidi); the audio/solfège
 * framing matches the plain scale-degree rung. See docs/design/guitar-harmony-v1.md.
 */

import { AudioPlanBuilder } from "./audio-plan.js";
import { Rng, type Exercise, type FretboardPrompt } from "./exercise.js";
import { appendCadence, degreeOffset, type CadenceOptions, type KeyChoice } from "./utils.js";
import { buildResolutionPlan } from "./generators/scale-degree.js";
import { resolveVoiceMidi } from "./exercise-fret-caged.js";
import {
  STANDARD_TUNING,
  CAGED_SHAPES,
  midiAt,
  positionsOnStringSet,
  type FretPosition,
  type CagedShape,
  type PitchClass,
} from "@music-app/theory-engine";

// ─── Params ─────────────────────────────────────────────────────────────────

export interface FretScaleDegreeCagedParams {
  /** The CAGED shape whose tonic-chord box frames the scale (E/A/G/C/D). */
  shape: CagedShape;
  /** 2..7 — the non-chord scale degree to place inside the box (re/fa/la/ti). */
  degree: number;
}

const MAJOR_SOLFEGE = ["do", "re", "mi", "fa", "sol", "la", "ti"] as const;

// ─── Key helper (pitch-class → KeyChoice, cadence in the middle octave) ───────

const PC_NAMES = ["C","D♭","D","E♭","E","F","F♯","G","A♭","A","B♭","B"] as const;

function buildMajorKey(tonicMidi: number): KeyChoice {
  const pc = ((tonicMidi % 12) + 12) % 12;
  const pcName = PC_NAMES[pc]!;
  const hasFlat = pcName.includes("♭");
  const hasSharp = pcName.includes("♯");
  const letter = pcName.replace(/[♭♯]/u, "");
  const accidental = hasFlat ? -1 : hasSharp ? 1 : 0;
  const accSym = hasFlat ? "♭" : hasSharp ? "♯" : "";
  return {
    letter,
    accidental,
    mode: "ionian",
    tonicMidi: 60 + pc, // normalise to the middle octave for the cadence
    name: `${letter}${accSym} major`,
  };
}

// ─── Generator ────────────────────────────────────────────────────────────────

export function generateFretScaleDegreeCaged(
  itemId: string,
  params: FretScaleDegreeCagedParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);

  const shapeDef = CAGED_SHAPES[params.shape];
  if (!shapeDef) {
    throw new Error(`CAGED shape not implemented: ${params.shape}`);
  }
  const { canonicalRootString, pattern } = shapeDef;
  const minRootFret = shapeDef.minRootFret ?? 2;

  // The lit shape is the tonic (I) chord: its root fret fixes the key. The scale
  // degrees are then placed relative to this tonic, inside the shape's box.
  const rootFret = minRootFret + rng.int(12 - minRootFret + 1);
  const tonicMidi = midiAt(STANDARD_TUNING, { stringIndex: canonicalRootString, fret: rootFret });
  const key = buildMajorKey(tonicMidi);
  const syllable = MAJOR_SOLFEGE[params.degree - 1] ?? `${params.degree}`;

  // Resolve every voice of the tonic triad (major quality) — these are the lit
  // chord-tone dots (do/mi/sol) that frame the box.
  const given: FretPosition[] = [];
  let minFret = rootFret;
  let maxFret = rootFret;
  for (const [stringIndex, voice] of pattern) {
    const targetMidi = resolveVoiceMidi(tonicMidi, voice, "maj");
    const openMidi = midiAt(STANDARD_TUNING, { stringIndex, fret: 0 });
    const fret = targetMidi - openMidi;
    if (fret < 0 || fret > STANDARD_TUNING.fretCount) continue;
    given.push({ stringIndex, fret });
    minFret = Math.min(minFret, fret);
    maxFret = Math.max(maxFret, fret);
  }

  // The box: shape extent ±1 fret. The answer must live inside it.
  const fretWindow = {
    from: Math.max(0, minFret - 1),
    to: Math.min(STANDARD_TUNING.fretCount, maxFret + 1),
  };

  // Accepted answers: every position of the target degree's pitch class inside
  // the box, on any string. Degrees 2/4/6/7 never collide with the lit chord
  // tones (1/3/5), so `given` and `acceptedPositions` are always disjoint.
  const allStrings = STANDARD_TUNING.openStrings.map((_, i) => i);
  const targetPc = ((((tonicMidi + degreeOffset(params.degree, "major")) % 12) + 12) % 12) as PitchClass;
  const acceptedPositions = positionsOnStringSet(STANDARD_TUNING, targetPc, allStrings).filter(
    (p) => p.fret >= fretWindow.from && p.fret <= fretWindow.to,
  );

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given,
    notesToPlace: 1,
    acceptedPositions,
    solution: acceptedPositions,
    listenFretWindow: fretWindow,
  };

  // Audio: cadence establishes the key, then the target degree an octave above
  // the cadence-register tonic — the same framing as the plain scale-degree rung.
  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  const audioTargetMidi = key.tonicMidi + 12 + degreeOffset(params.degree, "major");
  builder.addNote(audioTargetMidi, 1.4, 0.22, "target");
  builder.advance(1.5);
  const audio = builder.build();

  return {
    itemId,
    exerciseType: "fret-scale-degree-caged-recognition",
    audio,
    prompt: `Tap ${syllable} in the ${params.shape} shape`,
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `Scale degree ${params.degree} (${syllable}) inside the ${params.shape}-shape box in ${key.name}.`,
      distractorCount: 0,
      tonicMidi, // drives relative solfège on the dots
      tonalityMode: "major",
      teachAudio: buildResolutionPlan(key, params.degree),
      fretboard,
      playbackMidis: [audioTargetMidi],
    },
  };
}
